import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useGraphViewPerformanceTracking } from 'tabview/graphComponent/ArdoqGraphComponent';
import {
  canBypassLimit,
  isAboveItemLimit,
} from 'tabview/graphComponent/graphItemLimit';
import { ScenarioSyncedSettingsBar as SettingsBar } from './view/ScenarioSyncedSettingsBar';
import { PersonalSetting, ViewIds } from '@ardoq/api-types';
import {
  Cursor,
  GraphComponent,
  GraphInputMode,
  GraphViewerInputMode,
  Command,
  IGraph,
} from '@ardoq/yfiles';
import { getViewModel$ } from './viewModel$';
import type { BlockDiagramViewProperties } from './types';
import { CollapsibleGroupsYGraphBuilder } from './view/yFilesExtensions/CollapsibleGroupsYGraphBuilder';
import { onLayoutGraph as onLayoutGraphHandler } from './view/yFilesExtensions/onLayoutGraph';
import { configureLayout } from './view/configureLayout';
import { ZoomControl } from 'atomicComponents/Zoomable/ZoomControls';
import { IconName } from '@ardoq/icons';
import { t } from '@ardoq/i18n';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  relationshipDiagramBypassDataLimit,
  relationshipDiagramReset,
} from 'tabview/relationshipDiagrams/actions';
import { canBypassViewDataLimit } from 'views/environmentals/viewDataLimits';
import { BlockDiagramVisualDiffSwitcher } from './view/BlockDiagramVisualDiffSwitcher';
import { getNodeSize, getStyle } from 'yfilesExtensions/styles/styleLoader';
import { MainPaneLoader } from 'streams/views/mainContent/MainPaneLoader';
import { WithLoadingIndicator } from 'tabview/WithLoadingIndicator';
import { notifyViewLoading } from 'tabview/actions';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { ViewAndLegendContainer } from 'tabview/ViewAndLegendContainer';
import { defaultLegendOnClick } from 'viewSettings/getRightMenuConfig';
import { EmptyState as EmptyStateBase } from './EmptyState';
import {
  getAddedAndUpdatedItems,
  getGraphNodeCount,
  hoverPathCss,
} from '@ardoq/graph';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import { notifyExportFailure } from '@ardoq/export';
import { getExportLabels } from 'views/getExportLabels';
import type { OnLayoutGraphArgs } from 'tabview/graphComponent/onLayoutGraph';
import {
  DisabledZoomControls,
  updateViewContext,
  type ViewContext,
} from '@ardoq/view-settings';
import { isPresentationMode } from 'env';
import {
  ArdoqGraphComponentWithLegend,
  ArdoqGraphComponentWrapperForwardRefType,
} from 'tabview/ArdoqGraphComponentWithLegend';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.BLOCK_DIAGRAM;

const Container = styled.div`
  path.hoverPath {
    opacity: 0;
    ${hoverPathCss}
  }
`;

const getGraphBuilder = (graph: IGraph, viewInstanceId: string) =>
  new CollapsibleGroupsYGraphBuilder(
    VIEW_ID,
    viewInstanceId,
    graph,
    getStyle,
    getNodeSize
  );

const configureRendering = (graphComponent: GraphComponent) => {
  const inputMode = graphComponent.inputMode as GraphViewerInputMode;
  inputMode.navigationInputMode.enabled = false;
  inputMode.defaultCursor = Cursor.GRAB;
  graphComponent.maximumZoom = 1.6;
  graphComponent.minimumZoom = 0.01;
  graphComponent.mouseWheelZoomFactor = 1.1;
};

const BLOCK_DIAGRAM_HOVER_MODE = {
  edgeHover: true,
  highlightAdjacent: true,
  transparentizeNonAdjacent: false,
};

export const BlockDiagramView = (props: BlockDiagramViewProperties) => {
  const ardoqGraphComponentRef =
    useRef<ArdoqGraphComponentWrapperForwardRefType>(null);

  const graphBuilder = useRef<CollapsibleGroupsYGraphBuilder | null>(null);

  const {
    viewModel,
    viewInstanceId,
    viewSettings,
    scenarioName,
    noContext,
    isViewpointMode,
    clearEdges,
    performanceTrackingId,
    scopeDiffState,
    streamState: { loadedScenarioData },
    errorInfoBoxLabels,
    i18nLocale,
    legendProps,
    dataLimitExceeded,
    hasUserBypassedDataLimit,
  } = props;

  const buildGraph = useCallback(
    (
      graphComponent: GraphComponent,
      bypassLimit: boolean,
      clearEdges: boolean
    ) => {
      graphBuilder.current =
        graphBuilder.current ||
        getGraphBuilder(graphComponent.graph, viewInstanceId);
      graphBuilder.current.updateScenarioData(loadedScenarioData);

      setTimeout(() =>
        // This call dispatches a reducer action which accordingly
        // triggers a render in the navigator component which results in this kind
        // of warning:
        //
        // createActionStream.ts:18 Warning: Cannot update a component (`Connect`)
        // while rendering a different component (`ForwardRef`). To locate the bad
        // setState() call inside `ForwardRef`, follow the stack trace as described
        // in https://reactjs.org/link/setstate-in-render
        // at eval (webpack-internal:///./tabview/graphComponent/ArdoqGraphComponent.tsx:93:11)
        //
        // The timeout is more a workaround than a proper fix.
        (graphComponent.inputMode as GraphInputMode).clearSelection()
      );
      if (clearEdges) {
        graphBuilder.current.clearEdges();
      }
      const groups = getAddedAndUpdatedItems(viewModel.groups);

      const nodes = getAddedAndUpdatedItems(viewModel.nodes);
      const edges = getAddedAndUpdatedItems(viewModel.edges);

      const graphItemLimitArgs = {
        viewId: VIEW_ID,
        nodeCount: nodes.length,
        edgeCount: edges.length,
        presentationMode: isPresentationMode(),
      };
      const aboveLimit = dataLimitExceeded
        ? true
        : isAboveItemLimit(graphItemLimitArgs);
      const canBypass = dataLimitExceeded
        ? canBypassViewDataLimit(VIEW_ID, dataLimitExceeded.itemCount)
        : canBypassLimit(graphItemLimitArgs);
      const isEmpty = !nodes.length && !edges.length && !groups.length;
      const hasLayoutUpdate = graphBuilder.current.hasLayoutUpdate({
        groups,
        nodes,
        edges,
      });
      if ((!bypassLimit && aboveLimit) || isEmpty) {
        graphBuilder.current.groupsSource = [];
        graphBuilder.current.nodesSource = [];
        graphBuilder.current.edgesSource = [];
        graphBuilder.current.clear();
      } else {
        graphBuilder.current.groupsSource = groups;
        graphBuilder.current.nodesSource = nodes;
        graphBuilder.current.edgesSource = edges;
        graphBuilder.current.updateGraph();
      }

      return { isAboveLimit: aboveLimit, isEmpty, canBypass, hasLayoutUpdate };
    },
    [viewModel, viewInstanceId, loadedScenarioData, dataLimitExceeded]
  );
  const getContainer = () => containerRef.current ?? null;
  const getGraphComponentRef = () =>
    ardoqGraphComponentRef.current?.graphComponent ?? null;
  const getGraphComponent = () => getGraphComponentRef()?.current ?? null;
  const optimizeLayout = useCallback(
    () => dispatchAction(relationshipDiagramReset()),
    []
  );
  const onBypassDataLimit = useCallback(
    () =>
      dispatchAction(
        relationshipDiagramBypassDataLimit({ viewId: VIEW_ID }),
        viewInstanceId
      ),
    [viewInstanceId]
  );
  const toggleLegend = () => {
    const nextIsLegendActive = !renderingParameters.current.isLegendActive;
    defaultLegendOnClick({
      isLegendActive: nextIsLegendActive,
      viewId: VIEW_ID,
    });
    renderingParameters.current.isLegendActive = nextIsLegendActive;
    dispatchAction(updateViewContext(renderingParameters.current));
  };

  useEffect(() => {
    if (!getGraphComponentRef()) {
      graphBuilder.current = null;
    }

    const currentGraphNodeCount = getGraphNodeCount(viewModel);

    if (!currentGraphNodeCount || viewModel.noConnectedComponents) {
      // normally isBusy=false is dispatched in the graph layout callback, but in this case we have no graph, so we must dispatch isBusy=false
      setTimeout(() =>
        dispatchAction(
          notifyViewLoading(viewInstanceId, {
            isBusy: false,
          })
        )
      );
    }
  }, [viewInstanceId, viewModel]);

  const isLegendActive = viewSettings.isLegendActive ?? false;
  const renderingParameters = useRef({
    getContainer,
    getGraphComponent,
    optimizeLayout,
    zoomIn: () =>
      getGraphComponent()?.executeCommand(Command.INCREASE_ZOOM, 1.2),
    zoomOut: () =>
      getGraphComponent()?.executeCommand(Command.DECREASE_ZOOM, 1.2),
    toggleLegend,
    isLegendActive,
    zoomControls: null,
    zoomControlsDisabledState: DisabledZoomControls.NONE,
    viewInstanceId,
  } satisfies ViewContext);
  useEffect(() => {
    renderingParameters.current.isLegendActive = isLegendActive;
    dispatchAction(updateViewContext(renderingParameters.current));
  }, [isLegendActive]);
  useEffect(() => {
    dispatchAction(updateViewContext(renderingParameters.current));
    const graphComponent = getGraphComponent();
    if (!graphComponent) {
      return;
    }
    const updateZoomControlsDisabledState = () => {
      const { zoom, minimumZoom, maximumZoom } = graphComponent;
      const isZoomOutDisabled = zoom - Number.EPSILON <= minimumZoom;
      const isZoomInDisabled = zoom + Number.EPSILON >= maximumZoom;
      const zoomControlsDisabledState =
        (Number(isZoomOutDisabled) && DisabledZoomControls.ZOOM_OUT) |
        (Number(isZoomInDisabled) && DisabledZoomControls.ZOOM_IN);

      dispatchAction(
        updateViewContext({
          ...renderingParameters.current,
          zoomControlsDisabledState,
        })
      );
    };
    graphComponent.addEventListener(
      'viewport-changed',
      updateZoomControlsDisabledState
    );
    return () =>
      graphComponent.removeEventListener(
        'viewport-changed',
        updateZoomControlsDisabledState
      );
  });
  const {
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
    referenceTypes,
    noConnectedComponents,
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    expandedStateChangingGroupId,
    hasClones,
    errors,
  } = viewModel;

  const activeGroups = viewModel.groups.add.concat(viewModel.groups.update);

  const isEmptyView =
    !dataLimitExceeded &&
    (!getGraphNodeCount(viewModel) || noConnectedComponents || noContext);

  const isScopeLoading = scopeDiffState.isLoading;

  const [clearedErrors, setClearedErrors] = useState(false);
  const [clearedHasClones, setClearedHasClones] = useState(false);
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_BLOCK_DIAGRAM
  );

  const onLayoutGraph = useCallback(
    (args: OnLayoutGraphArgs) => {
      onLayoutGraphHandler(expandedStateChangingGroupId)(args);
      const { layoutDescriptor } = args;
      const isFreshLayout =
        layoutDescriptor.name === 'HierarchicalLayout' &&
        !layoutDescriptor.properties?.fromSketchMode;
      if (isFreshLayout) {
        dispatchAction(
          updateViewContext({
            ...renderingParameters.current,
            zoomControlsDisabledState: DisabledZoomControls.FIT_WINDOW,
          })
        );
      }
    },
    [expandedStateChangingGroupId]
  );

  const buildGraphCallback = useCallback(
    (graphComponent: GraphComponent, bypassLimit: boolean) =>
      buildGraph(graphComponent, bypassLimit, clearEdges),
    [buildGraph, clearEdges]
  );

  const { layoutOrientation, separateReferences } = viewSettings;
  const configureLayoutCallback = useCallback(
    (graphComponent: GraphComponent) => {
      return configureLayout(graphComponent, viewModel, {
        layoutOrientation,
        separateReferences,
      });
    },
    [viewModel, layoutOrientation, separateReferences]
  );

  const additionalZoomControls = useMemo(
    () => (
      <ZoomControl
        iconName={IconName.OPTIMIZE_LAYOUT}
        onClick={optimizeLayout}
        label={t(
          i18nLocale,
          'tabview:blockDiagram.view.optimizeLayout',
          'Optimize layout'
        )}
      />
    ),
    [optimizeLayout, i18nLocale]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <Container className={`tab-pane ${VIEW_ID}Tab active`}>
      {isViewpointMode ? null : (
        <div className="menuContainer">
          <SettingsBar
            viewId={VIEW_ID}
            getLeftMenuArgs={{
              traversedOutgoingReferenceTypes,
              traversedIncomingReferenceTypes,
              referenceTypes,
              viewId: VIEW_ID,
              onViewSettingsUpdate,
              activeGroups,
              isViewpointMode,
            }}
            getRightMenuArgs={{
              getContainer,
              getGraphComponent,
              viewId: VIEW_ID,
              legendOnClick: toggleLegend,
              onViewSettingsUpdate,
              isEmptyView,
              showExportFailureModal: format =>
                notifyExportFailure(format, getExportLabels(i18nLocale)),
            }}
            viewInstanceId={viewInstanceId}
            viewSettings={viewSettings}
            i18nLocale={i18nLocale}
          />
        </div>
      )}
      <ViewAndLegendContainer ref={containerRef}>
        {isEmptyView ? (
          <EmptyStateBase
            noConnectedComponents={noConnectedComponents}
            noContext={noContext}
          />
        ) : (
          <>
            <MainPaneLoader isLoading={isScopeLoading} />
            <ArdoqGraphComponentWithLegend
              viewId={VIEW_ID}
              viewInstanceId={viewInstanceId}
              ref={ardoqGraphComponentRef}
              viewModel={viewModel}
              legendProps={legendProps}
              isLegendActive={isLegendActive}
              buildGraph={buildGraphCallback}
              enableStyles={true}
              configureLayout={configureLayoutCallback}
              configureRendering={configureRendering}
              onLayoutGraph={onLayoutGraph}
              performanceTrackingId={performanceTrackingId}
              hoverMode={BLOCK_DIAGRAM_HOVER_MODE}
              hideZoomControls={isViewpointMode && !isPresentationMode()}
              additionalZoomControls={additionalZoomControls}
              urlFieldValuesByComponentId={urlFieldValuesByComponentId}
              urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
              isViewpointMode={isViewpointMode}
              i18nLocale={i18nLocale}
              hasUserBypassedDataLimit={hasUserBypassedDataLimit}
              onBypassDataLimit={onBypassDataLimit}
            />
            <ErrorInfoBox
              errors={clearedErrors ? [] : errors}
              hasClones={!neverShowAgain && !clearedHasClones && hasClones}
              clearHasClones={() => setClearedHasClones(true)}
              clearErrors={() => setClearedErrors(true)}
              isShowNeverAgainSet={neverShowAgain}
              toggleNeverShowAgain={toggleNeverShowAgain}
              isPresentationMode={isPresentationMode()}
              {...errorInfoBoxLabels}
            />
            {(scopeDiffState.scopeDiff || isScopeLoading) && (
              <BlockDiagramVisualDiffSwitcher
                viewId={ViewIds.BLOCK_DIAGRAM}
                scenarioName={scenarioName}
                activeDiffMode={viewSettings.activeDiffMode}
                i18nLocale={i18nLocale}
              />
            )}
          </>
        )}
      </ViewAndLegendContainer>
    </Container>
  );
};

const PerformanceTrackedBlockDiagram = (props: BlockDiagramViewProperties) => {
  useGraphViewPerformanceTracking(props.viewModel, VIEW_ID);
  return <BlockDiagramView {...props} />;
};

const BlockDiagramViewWithLoadingIndicator = (
  props: BlockDiagramViewProperties
) => (
  <WithLoadingIndicator
    viewInstanceId={props.viewInstanceId}
    loadingSource="notifyViewLoading"
    showContentWhileLoading
    i18nLocale={props.i18nLocale}
  >
    <PerformanceTrackedBlockDiagram {...props} />
  </WithLoadingIndicator>
);

export const ConnectedBlockDiagramView = connectInstanceWithProps(
  BlockDiagramViewWithLoadingIndicator,
  getViewModel$
);
